/**
 * AuditLogPanel - Production Audit Log Slide-out Sidebar
 * 
 * Displays a chronological timeline of all production events including:
 * - Script version uploads and changes
 * - Schedule updates
 * - Call sheet processing
 * - Daily reports (DPR, DSR, Hot Cost)
 * - Budget changes
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Divider,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as ScriptIcon,
  CalendarToday as ScheduleIcon,
  Assignment as CallSheetIcon,
  Movie as DprIcon,
  RateReview as DsrIcon,
  AttachMoney as HotCostIcon,
  AccountBalance as BudgetIcon,
  Event as EventIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { apiService, AuditLogEvent, AuditLogResponse } from '../../services/api';

interface AuditLogPanelProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  script: <ScriptIcon />,
  schedule: <ScheduleIcon />,
  call_sheet: <CallSheetIcon />,
  dpr: <DprIcon />,
  dsr: <DsrIcon />,
  hot_cost: <HotCostIcon />,
  budget: <BudgetIcon />,
  production: <EventIcon />,
};

const DRAWER_WIDTH = 400;

export const AuditLogPanel: React.FC<AuditLogPanelProps> = ({ open, onClose }) => {
  const [events, setEvents] = useState<AuditLogEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });
  const [categories, setCategories] = useState<Record<string, { label: string; icon: string; color: string }>>({});

  const fetchAuditLog = useCallback(async (reset: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const offset = reset ? 0 : pagination.offset;
      const response = await apiService.getAuditLog({
        limit: 50,
        offset,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
      });

      if (response.success && response.data) {
        const newEvents = response.data.events;
        setEvents(reset ? newEvents : [...events, ...newEvents]);
        setPagination(response.data.pagination);
        setCategories(response.data.categories);
      } else {
        setError(response.error || 'Failed to load audit log');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Audit log fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, pagination.offset, events]);

  useEffect(() => {
    if (open) {
      fetchAuditLog(true);
    }
  }, [open, categoryFilter]);

  const handleLoadMore = () => {
    setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    fetchAuditLog(false);
  };

  const handleRefresh = () => {
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchAuditLog(true);
  };

  const formatEventDate = (timestamp: string): string => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const groupEventsByDate = (events: AuditLogEvent[]): Record<string, AuditLogEvent[]> => {
    const groups: Record<string, AuditLogEvent[]> = {};
    events.forEach(event => {
      const dateKey = formatEventDate(event.timestamp);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });
    return groups;
  };

  const groupedEvents = groupEventsByDate(events);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#1a1a2e',
          borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: '#16213e',
        }}
      >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          Production Log
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filter */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            <FilterIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            Filter by Category
          </InputLabel>
          <Select
            value={categoryFilter}
            label="Filter by Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
              },
              '& .MuiSvgIcon-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          >
            <MenuItem value="all">All Events</MenuItem>
            <MenuItem value="script">Scripts</MenuItem>
            <MenuItem value="schedule">Schedules</MenuItem>
            <MenuItem value="call_sheet">Call Sheets</MenuItem>
            <MenuItem value="dpr">DPR Reports</MenuItem>
            <MenuItem value="dsr">DSR Reports</MenuItem>
            <MenuItem value="hot_cost">Hot Cost Reports</MenuItem>
            <MenuItem value="budget">Budget</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {loading && events.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress sx={{ color: '#3498db' }} />
          </Box>
        ) : events.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <EventIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography>No events recorded yet</Typography>
          </Box>
        ) : (
          <>
            {Object.entries(groupedEvents).map(([dateKey, dateEvents]) => (
              <Box key={dateKey}>
                {/* Date Header */}
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#16213e',
                    py: 1,
                    px: 2,
                    zIndex: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}
                  >
                    {dateKey}
                  </Typography>
                </Box>

                {/* Events for this date */}
                <List disablePadding>
                  {dateEvents.map((event, index) => (
                    <ListItem
                      key={event.id}
                      sx={{
                        borderBottom:
                          index < dateEvents.length - 1
                            ? '1px solid rgba(255, 255, 255, 0.05)'
                            : 'none',
                        alignItems: 'flex-start',
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          mt: 0.5,
                          color: event.categoryColor,
                        }}
                      >
                        {CATEGORY_ICONS[event.category] || <EventIcon />}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: 'white', fontWeight: 500, flex: 1 }}
                            >
                              {event.title}
                            </Typography>
                            {event.shootDay && (
                              <Chip
                                label={`Day ${event.shootDay}`}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  backgroundColor: 'rgba(52, 152, 219, 0.2)',
                                  color: '#3498db',
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {event.description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  display: 'block',
                                  mb: 0.5,
                                }}
                              >
                                {event.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={event.categoryLabel}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  backgroundColor: `${event.categoryColor}20`,
                                  color: event.categoryColor,
                                  border: `1px solid ${event.categoryColor}40`,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255, 255, 255, 0.4)' }}
                              >
                                {event.eventTime}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}

            {/* Load More */}
            {pagination.hasMore && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loading}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Footer with count */}
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: '#16213e',
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', display: 'block' }}
        >
          Showing {events.length} of {pagination.total} events
        </Typography>
      </Box>
    </Drawer>
  );
};

export default AuditLogPanel;
